import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BeginnerSidebar from "./BeginnerSidebar";
import { getBeginnerMainExams, unwrapRows } from "../api/beginnerExamClient";
import "../styles/practice.css";
import "../styles/BeginnerExam.css";
import "../styles/BeginnerButtons.css";

/*
 * This page is the Beginner Main Exam landing/list page.
 * It must NOT create an attempt or start the exam on mount.
 * Students first select an exam, review its criteria, and explicitly continue.
 */
export default function BeginnerMainExam() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    getBeginnerMainExams()
      .then((response) => {
        if (!active) return;
        const rows = unwrapRows(response);
        setExams(rows);
        if (!rows.length) {
          setError("No main exam is currently available for your account.");
        }
      })
      .catch((err) => {
        if (!active) return;
        setError(
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to load your main exams. Please try again."
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="oes-page-full practice-page beginner-dashboard">
      <div className="oes-home-shell d-flex flex-row">
        <BeginnerSidebar />
        <main className="flex-grow-1 oes-page container-fluid px-3 px-md-4 py-4">
          <header className="practice-heading">
            <div>
              <span className="practice-eyebrow">BEGINNER EXAMINATION</span>
              <h1>Main Exam</h1>
              <p>Review the available examination and its rules before starting.</p>
            </div>
          </header>

          {loading ? (
            <section className="practice-setup-card" aria-live="polite">
              Loading available exams…
            </section>
          ) : error ? (
            <section className="practice-setup-card">
              <p role="alert">{error}</p>
              <button className="beginner-btn beginner-btn-secondary" type="button" onClick={() => navigate("/beginner/dashboard")}>
                Back to Dashboard
              </button>
            </section>
          ) : (
            <section className="practice-setup-card">
              <div className="practice-card-heading">
                <div className="practice-icon-tile">
                  <i className="bi bi-file-earmark-text" />
                </div>
                <div>
                  <h2>Available Main Exams</h2>
                  <p>Select an exam to read the criteria and confirm before entering.</p>
                </div>
              </div>

              <div className="beginner-dashboard-grid">
                {exams.map((exam) => {
                  const id = exam.id ?? exam.exam_access_id;
                  const name = exam.exam_name || exam.name || exam.title || "Beginner Main Exam";
                  const duration =
                    exam.exam_duration_minutes ||
                    exam.duration_minutes ||
                    exam.duration ||
                    null;

                  return (
                    <article className="beginner-dashboard-card" key={String(id)}>
                      <span className="beginner-dashboard-card-icon">
                        <i className="bi bi-journal-check" />
                      </span>
                      <span className="beginner-dashboard-card-title">{name}</span>
                      <span className="beginner-dashboard-card-description">
                        {duration ? `${duration} minutes` : "Official examination"}
                      </span>
                      <button
                        type="button"
                        className="beginner-dashboard-card-action beginner-btn beginner-btn-primary"
                        onClick={() => navigate(`/beginner/exam-criteria/${id}`)}
                      >
                        View criteria <i className="bi bi-arrow-right" />
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
